import { useMemo, useState } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import { showToast } from '@components/toaster';
import { BtnPrimary, BtnSecondary, BtnCopy } from '@components/buttons';
import { IcoExternalLink } from '@components/icons';
import { Note } from '@course/components/note';
import { RichHtmlField, trimEmptyHtml } from '@components/minidoc/rich-html-field';
import { fullyQualify } from 'client/utils/url';
import { FreeEnrollmentConfig } from 'server/types/pmts-overrides';

const store = rpx.prices;

type Props = {
  priceId: string;
  productId: string;
  courseId?: string;
  config?: FreeEnrollmentConfig;
  onSave(updatedConfig: FreeEnrollmentConfig): void;
};

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function firstOfThisMonth(): string {
  const d = new Date();
  d.setDate(1);
  return toIsoDate(d);
}

function todayLocal(): string {
  return toIsoDate(new Date());
}

function emptyConfig(): FreeEnrollmentConfig {
  return {
    templateMode: 'free-enrollment',
    hero: {
      headline: '',
      subtitle: '',
    },
    bodyHtml: '',
    ctaText: '',
    successHeadline: '',
    successBodyHtml: '',
    successCtaText: '',
    alreadyEnrolledMessage: '',
  };
}

function trimEmpty(value?: string): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function normalizeForSave(draft: FreeEnrollmentConfig): FreeEnrollmentConfig {
  return {
    templateMode: 'free-enrollment',
    hero: {
      headline: draft.hero.headline.trim(),
      subtitle: trimEmpty(draft.hero.subtitle),
      logoUrl: trimEmpty(draft.hero.logoUrl),
    },
    bodyHtml: trimEmptyHtml(draft.bodyHtml),
    ctaText: trimEmpty(draft.ctaText),
    alreadyEnrolledMessage: trimEmpty(draft.alreadyEnrolledMessage),
    successHeadline: trimEmpty(draft.successHeadline),
    successBodyHtml: trimEmptyHtml(draft.successBodyHtml),
    successCtaText: trimEmpty(draft.successCtaText),
  };
}

export function EnrollmentPageTab({ priceId, productId, courseId, config, onSave }: Props) {
  const [draft, setDraft] = useState<FreeEnrollmentConfig>(() => config || emptyConfig());
  const [isSaving, setIsSaving] = useState(false);
  const [exportStart, setExportStart] = useState(firstOfThisMonth);
  const [exportEnd, setExportEnd] = useState(todayLocal);
  const isConfigured = !!config;
  const enrollUrl = `/enroll/${priceId}`;
  const exportUrl = useMemo(() => {
    if (!courseId) {
      return undefined;
    }
    const params = new URLSearchParams({
      type: 'course',
      id: courseId,
      data: 'students',
      priceId,
      start: exportStart,
      end: `${exportEnd} 23:59:59`,
    });
    return `/csv/signups?${params.toString()}`;
  }, [courseId, priceId, exportStart, exportEnd]);

  function patchHero(patch: Partial<FreeEnrollmentConfig['hero']>) {
    setDraft((d) => ({ ...d, hero: { ...d.hero, ...patch } }));
  }

  function patch(patchObj: Partial<FreeEnrollmentConfig>) {
    setDraft((d) => ({ ...d, ...patchObj }));
  }

  async function handleSave(e: Event) {
    e.preventDefault();
    if (!draft.hero.headline.trim()) {
      showError(new Error('Headline is required.'));
      return;
    }
    setIsSaving(true);
    try {
      const normalized = normalizeForSave(draft);
      await store.updatePrice({
        priceId,
        productId,
        checkoutPageConfig: normalized,
      });
      setDraft(normalized);
      onSave(normalized);
      showToast({
        type: 'ok',
        title: 'Saved',
        message: 'Enrollment page saved.',
      });
    } catch (err) {
      showError(err);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form class="flex flex-col gap-8 max-w-2xl py-4" onSubmit={handleSave}>
      <Note>
        This is a free enrollment page for this price point. Share the link below with members to
        let them enroll in one click. Logged-out visitors can enroll with just their name and email
        — no password required.
      </Note>

      {isConfigured && (
        <section class="p-6 rounded-md border bg-gray-50">
          <h3 class="font-medium mb-4">Enrollment page link</h3>
          <div class="flex flex-col gap-2">
            <a class="flex items-center" href={enrollUrl} rel="noreferrer" target="_blank">
              {fullyQualify(enrollUrl)}
              <IcoExternalLink class="w-4 h-4 opacity-75 ml-2" />
            </a>
            <BtnCopy
              value={fullyQualify(enrollUrl)}
              class="text-center justify-center border border-gray-300 hover:bg-gray-100 active:bg-gray-200 hover:border-transparent rounded-md w-fit"
              margin="m-0"
            >
              Copy enrollment link
            </BtnCopy>
          </div>
        </section>
      )}

      {isConfigured && exportUrl && (
        <section class="p-6 rounded-md border bg-gray-50 flex flex-col gap-4">
          <div>
            <h3 class="font-medium">Download enrollments</h3>
            <p class="text-sm text-gray-600 mt-1">
              Export the people who enrolled via this page within a date range — useful for calendar
              invites or follow-ups.
            </p>
          </div>
          <div class="grid grid-cols-2 gap-3 max-w-sm">
            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium">From</span>
              <input
                type="date"
                class="inline-ruz-input"
                value={exportStart}
                onInput={(e) => setExportStart((e.target as HTMLInputElement).value)}
              />
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium">To</span>
              <input
                type="date"
                class="inline-ruz-input"
                value={exportEnd}
                onInput={(e) => setExportEnd((e.target as HTMLInputElement).value)}
              />
            </label>
          </div>
          <BtnSecondary type="button" href={exportUrl} class="w-fit">
            Download CSV
          </BtnSecondary>
        </section>
      )}

      <section class="flex flex-col gap-4">
        <h3 class="font-medium">Hero</h3>

        <label class="flex flex-col gap-1">
          <span class="font-medium">Headline</span>
          <input
            type="text"
            class="inline-ruz-input"
            value={draft.hero.headline}
            onInput={(e) => patchHero({ headline: (e.target as HTMLInputElement).value })}
            placeholder="Get instant access to AI Time Machine"
            maxLength={200}
            required
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="font-medium">Subtitle</span>
          <input
            type="text"
            class="inline-ruz-input"
            value={draft.hero.subtitle || ''}
            onInput={(e) => patchHero({ subtitle: (e.target as HTMLInputElement).value })}
            placeholder="Included with your AI Playground membership"
            maxLength={500}
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="font-medium">Logo URL (optional)</span>
          <input
            type="text"
            class="inline-ruz-input"
            value={draft.hero.logoUrl || ''}
            onInput={(e) => patchHero({ logoUrl: (e.target as HTMLInputElement).value })}
            placeholder="Leave blank to use the Mirasee logo"
            maxLength={2048}
          />
        </label>
      </section>

      <section class="flex flex-col gap-4">
        <h3 class="font-medium">Body</h3>

        <RichHtmlField
          label="Body content"
          hint="Optional — appears between the hero and the enrollment button."
          placeholderText="What members get when they enroll..."
          value={draft.bodyHtml || ''}
          onChange={(html) => patch({ bodyHtml: html })}
        />

        <label class="flex flex-col gap-1">
          <span class="font-medium">Button label</span>
          <input
            type="text"
            class="inline-ruz-input"
            value={draft.ctaText || ''}
            onInput={(e) => patch({ ctaText: (e.target as HTMLInputElement).value })}
            placeholder="Get instant access"
            maxLength={80}
          />
        </label>
      </section>

      <section class="flex flex-col gap-4">
        <h3 class="font-medium">After enrollment</h3>

        <label class="flex flex-col gap-1">
          <span class="font-medium">Success headline</span>
          <input
            type="text"
            class="inline-ruz-input"
            value={draft.successHeadline || ''}
            onInput={(e) => patch({ successHeadline: (e.target as HTMLInputElement).value })}
            placeholder="You're in!"
            maxLength={200}
          />
        </label>

        <RichHtmlField
          label="Success body"
          placeholderText="We're glad to have you. Click below to start."
          value={draft.successBodyHtml || ''}
          onChange={(html) => patch({ successBodyHtml: html })}
        />

        <label class="flex flex-col gap-1">
          <span class="font-medium">Success button label</span>
          <input
            type="text"
            class="inline-ruz-input"
            value={draft.successCtaText || ''}
            onInput={(e) => patch({ successCtaText: (e.target as HTMLInputElement).value })}
            placeholder="Go to the course"
            maxLength={80}
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="font-medium">Already-enrolled message</span>
          <input
            type="text"
            class="inline-ruz-input"
            value={draft.alreadyEnrolledMessage || ''}
            onInput={(e) => patch({ alreadyEnrolledMessage: (e.target as HTMLInputElement).value })}
            placeholder="You're already enrolled"
            maxLength={500}
          />
          <span class="text-xs text-gray-500">
            Shown when a member who already has access lands on this page.
          </span>
        </label>
      </section>

      <footer class="flex items-center gap-3">
        <BtnPrimary class="capitalize" isLoading={isSaving}>
          Save enrollment page
        </BtnPrimary>
        {isConfigured && (
          <BtnSecondary
            type="button"
            href={enrollUrl}
            target="_blank"
            rel="noreferrer"
            class="gap-2"
          >
            <IcoExternalLink />
            Preview
          </BtnSecondary>
        )}
      </footer>
    </form>
  );
}
